import { useCallback } from 'react';

/**
 * useAnnounce — send a polite announcement to the global aria-live region.
 *
 * Fixes audit #27: state changes ("Auto-saved", "Submitted", "Error") were
 * never announced to screen readers.
 *
 * Requires the App root to render:
 *   <div id="live-region" aria-live="polite" className="sr-only" />
 *
 * The clear-then-set pattern forces assistive tech to re-read identical
 * messages (e.g. consecutive "Saved" announcements).
 *
 * Usage:
 *   const announce = useAnnounce();
 *   announce('Review submitted');
 */
export const useAnnounce = (): ((message: string) => void) => {
  return useCallback((message: string) => {
    if (typeof document === 'undefined') return;
    const region = document.getElementById('live-region');
    if (!region) {
      // Quiet failure — log in dev so the missing root element is discoverable
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[useAnnounce] #live-region not found in DOM — skipping announcement:', message);
      }
      return;
    }
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  }, []);
};
